export type MailStatus = 'received' | 'deleted' | 'error';

export interface MailListItem {
  id: string;
  mailbox: string;
  from: string;
  to: string;
  subject: string;
  date: string | null;
  receivedAt: string;
  size: number;
  messageId: string | null;
  hasAttachment: boolean;
  status: MailStatus;
}

export interface AttachmentMeta {
  filename: string;
  mimeType: string;
  size: number;
  contentId: string | null;
  disposition: string | null;
  index: number;
}

export interface MailDetail extends MailListItem {
  headers: Record<string, string>;
  html: string | null;
  text: string | null;
  attachments: AttachmentMeta[];
  r2Key: string;
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface ApiSuccess<T> {
  success: true;
  data: T;
}

export interface ApiError {
  success: false;
  message: string;
}

export type ApiResponse<T> = ApiSuccess<T> | ApiError;

export interface MailboxItem {
  address: string;
  label: string;
  enabled: number;
  created_at: string;
  updated_at: string;
}

const TOKEN_KEY = 'cf_mail_inbox_token';
const API_BASE_KEY = 'cf_mail_inbox_api_base';

export function getStoredToken(): string {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getApiBase(): string {
  const fromStorage = localStorage.getItem(API_BASE_KEY);
  if (fromStorage) return fromStorage.replace(/\/+$/, '');
  const fromEnv = import.meta.env.VITE_API_BASE;
  if (fromEnv) return String(fromEnv).replace(/\/+$/, '');
  // 开发时走 Vite proxy；生产可在 Settings 配置 Worker URL
  return '';
}

export function setApiBase(base: string): void {
  localStorage.setItem(API_BASE_KEY, base.replace(/\/+$/, ''));
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken();
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${getApiBase()}${path}`, {
    ...init,
    headers,
  });

  // raw / attachment 可能不是 JSON
  const contentType = res.headers.get('Content-Type') || '';
  if (!contentType.includes('application/json')) {
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    return res as unknown as T;
  }

  const json = (await res.json()) as ApiResponse<T>;
  if (!json.success) {
    throw new Error(json.message || `HTTP ${res.status}`);
  }
  return json.data;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  mailbox?: string;
  from?: string;
  subject?: string;
  dateFrom?: string;
  dateTo?: string;
  q?: string;
}

export function listMails(params: ListParams = {}) {
  const sp = new URLSearchParams();
  if (params.page) sp.set('page', String(params.page));
  if (params.pageSize) sp.set('pageSize', String(params.pageSize));
  if (params.mailbox) sp.set('mailbox', params.mailbox);
  if (params.from) sp.set('from', params.from);
  if (params.subject) sp.set('subject', params.subject);
  if (params.dateFrom) sp.set('dateFrom', params.dateFrom);
  if (params.dateTo) sp.set('dateTo', params.dateTo);
  const qs = sp.toString();
  return request<{ items: MailListItem[]; pagination: PaginationMeta }>(
    `/api/mail${qs ? `?${qs}` : ''}`,
  );
}

export function searchMails(params: ListParams = {}) {
  const sp = new URLSearchParams();
  if (params.page) sp.set('page', String(params.page));
  if (params.pageSize) sp.set('pageSize', String(params.pageSize));
  if (params.q) sp.set('q', params.q);
  if (params.mailbox) sp.set('mailbox', params.mailbox);
  if (params.from) sp.set('from', params.from);
  if (params.subject) sp.set('subject', params.subject);
  const qs = sp.toString();
  return request<{ items: MailListItem[]; pagination: PaginationMeta }>(
    `/api/search${qs ? `?${qs}` : ''}`,
  );
}

export function getMail(id: string) {
  return request<MailDetail>(`/api/mail/${encodeURIComponent(id)}`);
}

export function deleteMail(id: string) {
  return request<{ id: string; deleted: boolean }>(`/api/mail/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export function listMailboxes() {
  return request<{ items: MailboxItem[] }>('/api/mailboxes');
}

export function upsertMailbox(body: { address: string; label?: string; enabled?: boolean }) {
  return request<{ address: string; label: string; enabled: boolean }>('/api/mailboxes', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function health() {
  return request<{ name: string; ok: boolean; time: string }>('/api/health');
}

/** 带鉴权下载 raw / 附件（返回 blob URL） */
export async function downloadAuthenticated(path: string, filename: string): Promise<void> {
  const token = getStoredToken();
  const res = await fetch(`${getApiBase()}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`下载失败 HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
